import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import path from 'path';
import { get as getValueAtPath } from 'lodash';
import { createHashHistory } from 'history';
import { Container, Row, Col, Button, Form } from 'reactstrap';
import { Prompt, useHistory } from 'react-router-dom';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { editPermissionSelector } from '../common/classes/permissions';
import entityComposition from '../common/classes/entityComposition';
import PopoverButton from '../common/controls/popoverButton';
import routeHelper from '../common/classes/routeHelper';
import SaveButton from '../common/controls/saveButton';
import navigation from '../common/pdf/navigation';
import Icon from '../common/containers/icon';
import IssueEditor from './issueEditor';
import Loader from '../common/loader';
import PDF from '../common/pdf/pdf';

// Default history that can be overridden in tests
const defaultHashHistory = createHashHistory();
const IssueDetailView = _ref => {
  let match = _ref.match,
    issueActions = _ref.issueActions,
    reportDocumentActions = _ref.reportDocumentActions,
    reportAnnotationActions = _ref.reportAnnotationActions,
    documentsButtonIndexQuery = _ref.documentsButtonIndexQuery,
    issue = _ref.issue,
    issuePopoverReportDocumentActions = _ref.issuePopoverReportDocumentActions,
    reportDocument = _ref.reportDocument,
    documentsButtonList = _ref.documentsButtonList,
    canEdit = _ref.canEdit,
    notificationActions = _ref.notificationActions,
    _ref$historyObj = _ref.historyObj,
    historyObj = _ref$historyObj === void 0 ? defaultHashHistory : _ref$historyObj,
    _ref$routeHelperObj = _ref.routeHelperObj,
    routeHelperObj = _ref$routeHelperObj === void 0 ? routeHelper : _ref$routeHelperObj,
    _ref$navigationObj = _ref.navigationObj,
    navigationObj = _ref$navigationObj === void 0 ? navigation : _ref$navigationObj,
    annotationSelectionActions = _ref.annotationSelectionActions;
  // Use useHistory from react-router-dom when available
  const routerHistory = useHistory();
  const history = routerHistory || historyObj;
  if (!match.params || !match.params.reportDocumentID) {
    throw new Error('props.match.params.reportDocumentID is required');
  }
  const _useState = useState(1200),
    _useState2 = _slicedToArray(_useState, 2),
    maxHeight = _useState2[0],
    setMaxHeight = _useState2[1];
  // Use ref for window access to make it easier to mock in tests
  const windowRef = useRef(typeof global !== 'undefined' && global.window ? global.window : {});

  // Helper to clear both issue and report annotation selections
  const clearAllAnnotationSelections = useCallback(() => {
    annotationSelectionActions.setAnnotationContext('issue');
    annotationSelectionActions.clearSelection();
    annotationSelectionActions.setAnnotationContext('report');
    annotationSelectionActions.clearSelection();
  }, [annotationSelectionActions]);

  // Convert handleSave method
  const handleSave = useCallback(() => {
    const version = issue.active && issue.active.content && issue.active.content.version || undefined;
    if (!issue.pendingChanges) {
      return;
    }
    issueActions.serverSave(version);
  }, [issue, issueActions]);

  // Extract cleanup function to make it more testable
  const handleCleanup = useCallback(() => {
    issueActions.undoChanges();
    reportAnnotationActions.undoChanges();
    issueActions.showCancel();
    clearAllAnnotationSelections();
  }, [issueActions, reportAnnotationActions, clearAllAnnotationSelections]);

  // Convert handleTransition method
  const handleTransition = useCallback(function () {
    let proceed = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : () => {};
    const entityIsMissing = issue && issue.error && issue.error.response && issue.error.response.status === 410;

    // if there are changes to save, ask the user what to do...
    if (issue.pendingChanges) {
      const noHandler = () => {
        handleCleanup();
        proceed();
      };

      // If the entity is missing we're going to navigate the user away
      if (entityIsMissing) {
        noHandler();
        return true;
      }
      if (canEdit) {
        history.replace(match.url);
        routeHelperObj.displayModal(handleSave, noHandler);
        return false; // tells prompt to block navigation
      }
      noHandler();
      const errorMsg = 'You are not authorized to modify this resource.';
      notificationActions.error({
        message: errorMsg
      });
      return true; // tells Prompt to allow navigation
    }
    issueActions.showCancel();
    clearAllAnnotationSelections();
    proceed();
    return true; // tells Prompt to allow navigation
  }, [issue, canEdit, match, notificationActions, handleSave, handleCleanup, history, issueActions, clearAllAnnotationSelections, routeHelperObj]);

  // Convert handleViewReportClick method
  const handleViewReportClick = useCallback(() => {
    const _documentsButtonList = _slicedToArray(documentsButtonList, 1),
      reportDocumentItem = _documentsButtonList[0];
    if (reportDocumentItem) {
      handleTransition(() => {
        const reportID = reportDocumentItem.reportID;
        issueActions.showCancel();
        clearAllAnnotationSelections();
        reportDocumentActions.reset();
        reportAnnotationActions.reset();
        const url = path.join('/reports', reportID);
        history.push(url);
      });
    }
  }, [documentsButtonList, issueActions, clearAllAnnotationSelections, reportDocumentActions, reportAnnotationActions, handleTransition, history]);

  // Convert handleReportDocumentClick method
  const handleReportDocumentClick = useCallback(_ref2 => {
    let id = _ref2.id;
    handleTransition(() => {
      const url = path.join('/issues/detailView', id);
      reportDocumentActions.reset();
      reportAnnotationActions.reset();
      reportDocumentActions.serverRenderReportDocument(id);
      annotationSelectionActions.setAnnotationContext('issue');
      annotationSelectionActions.setSelectableType('issue');
      history.push(url);
    });
  }, [reportDocumentActions, reportAnnotationActions, handleTransition, history, annotationSelectionActions]);

  // Convert handleIssueClick method
  const handleIssueClick = useCallback((clickedType, annotation) => {
    const reportDocumentID = match.params.reportDocumentID;
    const currentActiveIssueID = getValueAtPath(issue, 'active.id', null);

    // if an issue was selected (only gets passed up if it's selectable and not already selected)
    if (clickedType === 'issue') {
      const issueID = annotation.issue.id;
      handleTransition(() => {
        issueActions.serverShow(issueID);
        reportAnnotationActions.selectByAnnotationTypeID(issueID);
      });

      // if the selection was not an issue
    } else if (currentActiveIssueID && reportDocumentID) {
      handleTransition(() => {
        issueActions.showCancel();
        clearAllAnnotationSelections();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.reportDocumentID,
  // Using issue.active might still cause issues, so we use the disable comment above
  issueActions, reportAnnotationActions, handleTransition, history, annotationSelectionActions]);

  // Convert updateDimensions method
  const updateDimensions = useCallback(() => {
    if (!windowRef.current) return;

    // footer is 30 + 20
    // header is 59 + 30
    const verticalMargin = 139;
    if (windowRef.current.innerHeight < 2 * verticalMargin) {
      setMaxHeight(verticalMargin);
    } else {
      setMaxHeight(windowRef.current.innerHeight - verticalMargin);
    }
  }, []);

  // Convert handlePageNavigation method
  const handlePageNavigation = useCallback(() => {
    var _issue$active;
    const annotation = issue === null || issue === void 0 || (_issue$active = issue.active) === null || _issue$active === void 0 ? void 0 : _issue$active.reportAnnotation;
    if (annotation) {
      navigationObj.scrollAnnotationIntoView(annotation);
    }
  }, [issue, navigationObj]);

  // Setup event listeners for window resize
  useEffect(() => {
    // Update dimensions initially
    updateDimensions();

    // Store a reference to the current window to avoid issues with cleanup
    const currentWindow = windowRef.current;

    // Add event listener for window resize
    if (currentWindow) {
      currentWindow.addEventListener('resize', updateDimensions);
    }

    // Cleanup function
    return () => {
      if (currentWindow) {
        currentWindow.removeEventListener('resize', updateDimensions);
      }
    };
  }, [updateDimensions]);

  // Setup data fetching - separated from other effects
  useEffect(() => {
    var _issue$active2;
    const _match$params = match.params,
      reportDocumentID = _match$params.reportDocumentID,
      issueID = _match$params.issueID;

    // We use this inside the effect but don't want to trigger re-renders because of it
    // This is safe because we only care about the ID for comparison, and we're only using it
    // to decide whether to fetch, not for rendering
    const activeIssueId = issue === null || issue === void 0 || (_issue$active2 = issue.active) === null || _issue$active2 === void 0 ? void 0 : _issue$active2.id;

    // Grab the report document list to populate the popover button
    issuePopoverReportDocumentActions.serverIndex(_objectSpread(_objectSpread({}, documentsButtonIndexQuery), {}, {
      filters: {
        reportDocument: [{
          id: reportDocumentID
        }]
      }
    }));

    // Fetch the report document
    reportDocumentActions.serverRenderReportDocument(reportDocumentID);

    // Fetch issue if needed
    if (issueID && issueID !== activeIssueId) {
      issueActions.serverShow(issueID);
    }

    // Set up annotation selection
    const allowAnnotationsEdit = false; // not necessary, false = default, just being explicit
    annotationSelectionActions.setAnnotationContext('issue');
    annotationSelectionActions.setSelectableType('issue', allowAnnotationsEdit);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.reportDocumentID, match.params.issueID, documentsButtonIndexQuery, issueActions, reportDocumentActions, reportAnnotationActions, issuePopoverReportDocumentActions
  // Deliberately omitting issue to avoid infinite loops
  ]);

  // Separate cleanup effect
  useEffect(() => () => {
    // selection persists, sometimes to a new report doc if not cleared
    reportDocumentActions.reset();
    reportAnnotationActions.reset();
    issueActions.showCancel();
  }, [reportDocumentActions, reportAnnotationActions, issueActions]);

  // Render logic starts here
  const error = reportDocument.error,
    pdfPath = reportDocument.pdfPath,
    displayName = reportDocument.displayName,
    downloadPDFPath = reportDocument.downloadPDFPath;
  const loading = issue.loading,
    pendingChanges = issue.pendingChanges;

  // Move handleDownload and clearDownload into useCallback hooks
  const handleDownload = useCallback(() => {
    reportDocumentActions.serverRenderAnnotatedDocument(match.params.reportDocumentID);
  }, [reportDocumentActions, match.params.reportDocumentID]);
  const clearDownload = useCallback(() => {
    reportDocumentActions.clearDownloadPDFPath();
  }, [reportDocumentActions]);

  // Extract transformation to make it more testable
  const getTransformedDocumentsList = useCallback(docsList => {
    let maxLength = 0;
    return docsList.map(doc => {
      const displayLength = doc.displayName ? doc.displayName.length : 0;
      maxLength = Math.max(maxLength, displayLength);
      return _objectSpread(_objectSpread({}, doc), {}, {
        name: doc.displayName || '<no name provided>'
      });
    });
  }, []);
  const transformedList = getTransformedDocumentsList(documentsButtonList);
  const maxLength = transformedList.reduce((max, doc) => Math.max(max, doc.name.length), 0);
  const header = /*#__PURE__*/React.createElement("div", {
    style: {
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    className: "float-right",
    onClick: handleViewReportClick
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "eye"
  }), " View Report"));
  const button = /*#__PURE__*/React.createElement(PopoverButton, {
    width: maxLength * 10,
    header: header,
    selected: match.params.reportDocumentID,
    onSelect: handleReportDocumentClick,
    list: transformedList,
    testingIdentifier: "documents-button",
    title: "Documents"
  });
  const issueID = match.params.issueID || null;

  // Simplified save state logic
  const getSaveState = () => {
    if (!canEdit) return SaveButton.STATUS_DISABLED;
    if (pendingChanges) {
      return loading ? SaveButton.STATUS_IN_PROGRESS : SaveButton.STATUS_PENDING;
    }
    return SaveButton.STATUS_DISABLED;
  };
  const saveState = getSaveState();
  const pdfViewer = pdfPath ? /*#__PURE__*/React.createElement(PDF, {
    src: pdfPath,
    asyncDownloadPath: true,
    button: button,
    title: displayName,
    containerMaxHeight: maxHeight,
    onAnnotationSelect: handleIssueClick,
    preSelectedAnnotationTypeID: issueID,
    handleTransition: handleTransition,
    download: handleDownload,
    downloadPDFPath: downloadPDFPath,
    clearDownload: clearDownload
  }) : /*#__PURE__*/React.createElement("div", null, error ? "There was an error: ".concat(error) : /*#__PURE__*/React.createElement(Loader, {
    top: "25%"
  }));
  const footer = /*#__PURE__*/React.createElement(Form, null, /*#__PURE__*/React.createElement(Button, {
    disabled: issueID === null,
    className: "float-right",
    onClick: handlePageNavigation
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "crosshairs"
  }), " Scroll to Issue"));
  return /*#__PURE__*/React.createElement(Container, {
    fluid: true
  }, /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    xs: 12,
    sm: 9
  }, pdfViewer), /*#__PURE__*/React.createElement(Col, {
    xs: 12,
    sm: 3
  }, /*#__PURE__*/React.createElement(Prompt, {
    when: pendingChanges,
    message: location => handleTransition(() => history.replace(location.pathname))
  }), /*#__PURE__*/React.createElement(IssueEditor, {
    issueID: issueID,
    handleSave: handleSave,
    saveState: saveState,
    match: match,
    containerMaxHeight: maxHeight,
    footer: footer,
    width: "sm"
  }))));
};
IssueDetailView.propTypes = {
  documentsButtonList: PropTypes.arrayOf(PropTypes.shape({
    reportID: PropTypes.string,
    displayName: PropTypes.string
  })).isRequired,
  documentsButtonIndexQuery: PropTypes.shape({}).isRequired,
  issuePopoverReportDocumentActions: PropTypes.shape({
    serverIndex: PropTypes.func,
    serverFilter: PropTypes.func
  }).isRequired,
  issue: PropTypes.shape({
    error: PropTypes.shape({
      response: PropTypes.shape({
        status: PropTypes.number
      })
    }),
    active: PropTypes.shape({
      id: PropTypes.string,
      content: PropTypes.shape({
        version: PropTypes.string
      }),
      reportAnnotation: PropTypes.shape({
        id: PropTypes.string,
        page: PropTypes.number,
        position: PropTypes.shape({
          x: PropTypes.number,
          y: PropTypes.number
        })
      })
    }),
    pendingChanges: PropTypes.bool,
    loading: PropTypes.bool
  }).isRequired,
  issueActions: PropTypes.shape({
    serverShow: PropTypes.func,
    serverPatch: PropTypes.func,
    showCancel: PropTypes.func,
    serverSave: PropTypes.func,
    undoChanges: PropTypes.func
  }).isRequired,
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({
      reportDocumentID: PropTypes.string,
      issueID: PropTypes.string
    }).isRequired
  }).isRequired,
  reportDocument: PropTypes.shape({
    error: PropTypes.string,
    pdfPath: PropTypes.string,
    displayName: PropTypes.string,
    downloadPDFPath: PropTypes.string
  }).isRequired,
  reportDocumentActions: PropTypes.shape({
    serverRenderReportDocument: PropTypes.func,
    serverRenderAnnotatedDocument: PropTypes.func,
    clearDownloadPDFPath: PropTypes.func,
    reset: PropTypes.func
  }).isRequired,
  reportAnnotationActions: PropTypes.shape({
    selectByAnnotationTypeID: PropTypes.func,
    clearSelection: PropTypes.func,
    setSelectableType: PropTypes.func,
    reset: PropTypes.func,
    undoChanges: PropTypes.func
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired,
  // Optional props for testing
  historyObj: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    listen: PropTypes.func
  }),
  routeHelperObj: PropTypes.shape({
    displayModal: PropTypes.func
  }),
  navigationObj: PropTypes.shape({
    scrollAnnotationIntoView: PropTypes.func
  }),
  annotationSelectionActions: PropTypes.shape({
    setSelectableType: PropTypes.func.isRequired,
    clearSelection: PropTypes.func.isRequired,
    setAnnotationContext: PropTypes.func.isRequired
  }).isRequired
};
export { IssueDetailView as TestIssueDetailView };
export default entityComposition(IssueDetailView, ['reportDocument', 'issue'], {
  canEdit: state => editPermissionSelector('issue')(state),
  documentsButtonList: state => state.issuePopoverReportDocument.list,
  documentsButtonIndexQuery: state => state.issuePopoverReportDocument.indexQuery
}, ['reportAnnotation', 'issuePopoverReportDocument', 'notification', 'annotationSelection'], {
  annotationSelectionActions: 'annotationSelection'
});